import { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface QRScannerProps {
  onScan: (data: string) => void;
  onClose: () => void;
}

export function QRScanner({ onScan, onClose }: QRScannerProps) {
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const hasScannedRef = useRef(false);

  useEffect(() => {
    startScanner();

    return () => {
      stopScanner();
    };
  }, []);

  const startScanner = async () => {
    try {
      const scanner = new Html5Qrcode('qr-reader');
      scannerRef.current = scanner;

      await scanner.start(
        { facingMode: 'environment' }, // Use back camera
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
        },
        (decodedText) => {
          // Prevent multiple scans of the same QR code
          if (hasScannedRef.current) {
            return;
          }
          hasScannedRef.current = true;

          // QR Code successfully scanned
          onScan(decodedText);
          stopScanner();
        },
        (errorMessage) => {
          // Scanning error (happens frequently, can be ignored)
          // Only log severe errors
          if (!errorMessage.includes('NotFoundException')) {
            console.debug('QR scan error:', errorMessage);
          }
        }
      );

      setIsScanning(true);
    } catch (err: any) {
      console.error('Failed to start QR scanner:', err);
      setError(
        err.message || 'Failed to access camera. Please check permissions.'
      );
    }
  };

  const stopScanner = async () => {
    if (scannerRef.current && isScanning) {
      try {
        await scannerRef.current.stop();
        scannerRef.current.clear();
      } catch (err) {
        console.error('Failed to stop scanner:', err);
      }
      setIsScanning(false);
    }
  };

  const handleClose = () => {
    stopScanner();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-gray-900">
        <h2 className="text-xl font-bold text-white">Scan QR Code</h2>
        <button
          onClick={handleClose}
          className="text-white hover:text-gray-300"
        >
          <XMarkIcon className="w-6 h-6" />
        </button>
      </div>

      {/* Scanner */}
      <div className="flex-1 flex items-center justify-center p-4">
        {error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
            <h3 className="text-lg font-semibold text-red-900 mb-2">
              Camera Error
            </h3>
            <p className="text-red-700 mb-4">{error}</p>
            <button onClick={handleClose} className="btn btn-secondary">
              Close
            </button>
          </div>
        ) : (
          <div className="w-full max-w-md">
            <div
              id="qr-reader"
              className="rounded-lg overflow-hidden shadow-2xl"
            />
            <p className="text-white text-center mt-4">
              Position the QR code within the frame
            </p>
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="bg-gray-900 p-4 text-center">
        <p className="text-gray-400 text-sm">
          Hold your device steady and ensure the QR code is well-lit
        </p>
      </div>
    </div>
  );
}
